<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { STATUS_CLOSED, STATUS_OPEN } from '@/constants';
import { useViewComposable } from '@/views/composable';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ConfirmDeleteModal from '@/components/modal/ConfirmDeleteModal.vue';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';
import ImportFileModal from '@/components/modal/ImportFileModal.vue';
import ViewDestinationDataModal from '@/components/modal/ViewDestinationDataModal.vue';
import ViewVersionRevertModal from '@/components/modal/ViewVersionRevertModal.vue';
import ManageDestination from './ManageDestination.vue';
import ProgressModal from '@/components/modal/ProgressModal.vue';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();
const packageManagementStore = usePackageManagementStore();
const airManagementStore = useAirManagementStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const sheetsData = ref<any>([]);
const airports = ref<any>([]);
const { styleColumnExport, renderNameFileExport } = useViewComposable();
const widthList = [10, 40, 40, 12, 12, 10, 10, 15, 15, 20, 20, 15];
const sheetColumnStyle = styleColumnExport(widthList);
const filtersDest = ref();
const statuses = ref([STATUS_CLOSED, STATUS_OPEN]);
const isLoadingImport = ref(false);
const isShowModal = ref(false);
const isLoadingPage = ref(false);
const isSuccess = ref(false);
const isRevert = ref(false);

const destinations = computed(() => packageManagementStore.destinations);

const initFilters = () => {
  filtersDest.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    nameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    country: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    countryFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    airportName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    airportNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    region: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    regionFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    countryName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    countryNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    cityNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    cityName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    gatewayType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    airportCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    brochure: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    code: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFilters();

const editDestination = (data?: any) => {
  const dialogRef = dialog.open(ManageDestination, {
    data: {
      data: data,
      destinations,
      airports: airports.value
    },
    props: {
      header: data ? t('management.package.update_destination') : t('management.package.add_destination'),
      modal: true
    },
    emits: {
      closeModal: () => {
        dialogRef.close();
      },
      updateData: () => {
        getListDestination();
      }
    }
  });
};

const handleDelete = (payload) => {
  packageManagementStore
    .deleteDestination(payload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      getListDestination();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const handleDeleteDestination = (payload: { code: string }) => {
  dialog.open(ConfirmDeleteModal, {
    data: {
      title: t('management.package.confirm_delete')
    },
    props: {
      modal: true,
      style: { width: '400px' },
      header: t('common.confirm_delete')
    },
    emits: {
      onSave: async () => {
        handleDelete(payload);
      }
    }
  });
};

const clearFilter = () => {
  initFilters();
};

const formatDataExport = (listData?: any) => {
  const data = listData ?? destinations.value;
  if (data && data.length) {
    const listDataAfterHandle = data.map((item: any) => {
      const data: any = {};
      data[t('management.package.airport_code')] = item.airportCode;
      data[t('management.package.airport_name')] = item.airportName;
      data[t('management.air.airport_name_french')] = item.airportNameFr;
      data[t('management.package.destination_name')] = item.name;
      data[t('management.package.destination_name_french')] = item.nameFr;
      data[t('management.air.region')] = item.region;
      data[t('management.air.region_french')] = item.regionFr;
      data[t('management.air.country_name')] = item.country;
      data[t('management.air.country_name_french')] = item.countryFr;
      data[t('management.package.dest_status')] = item.status;
      data[t('management.package.dest_type')] = item.destType;
      data[t('management.package.dest_sv_id')] = item.code;
      data[t('management.package.brochure')] = item.brochure;
      return data;
    });
    sheetsData.value = [
      {
        name: 'Sheet 1',
        data: listDataAfterHandle
      }
    ];
    return listDataAfterHandle;
  }
  return [];
};

formatDataExport();

const getDestinationsConfiguration = (config: any) => {
  return config?.destination_configuration ?? {};
};

const onUpdateStatus = async (data: any) => {
  packageManagementStore
    .updateDestinationStatus(data)
    .then(() => {
      getListDestination();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const handleOpenChangeRole = (status: string) => {
  return (status === STATUS_OPEN && !authorizations?.configurationPage?.package_deactive) || (!authorizations?.configurationPage?.package_active && (status === STATUS_CLOSED || status === null));
};

const getSeverityStatus = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
    default:
      return '';
  }
};

const handleCloseModalProgress = (value: boolean) => {
  if (isRevert.value === true) {
    showRevertVersionDestination('DESTINATION');
  }
  isShowModal.value = value;
};

const showModalImportExcel = () => {
  const dialogRef = dialog.open(ImportFileModal, {
    props: {
      header: t('common.labels.upload_destinations'),
      modal: true
    },
    data: {
      url: packageManagementStore.getUrlUploadFileDestination
    },
    emits: {
      onApply: (data: any, file: any) => {
        handleSubmit(data, file, 'IMPORT');
        dialogRef.close();
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleSubmit = (data: any, file?: any, type?: string) => {
  const dialogRef = dialog.open(ViewDestinationDataModal, {
    props: {
      header: t('common.labels.review_upload_file'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      destinations: data,
      type,
      sheetData: formatDataExport(data)
    },
    emits: {
      onApply: () => {
        dialogRef.close();
        if (type === 'IMPORT') {
          handleImportFile(file);
        } else if (type === 'REVERT') {
          handleRevertFile(file);
        }
      },
      onClose: () => {
        dialogRef.close();
        if (type === 'REVERT') {
          showRevertVersionDestination('DESTINATION');
        }
      }
    }
  });
};

const handleImportFile = async (file: any) => {
  isLoadingImport.value = true;
  isShowModal.value = true;
  isRevert.value = false;
  const payload = new FormData();
  payload.append('file', file);
  packageManagementStore
    .importFileDestination(payload)
    .then(() => {
      isSuccess.value = true;
      getListDestination();
    })
    .catch(() => {
      isSuccess.value = false;
    })
    .finally(() => {
      isLoadingImport.value = false;
    });
};

const showRevertVersionDestination = async (type: string) => {
  packageManagementStore
    .getVersionRevert(type)
    .then((versions: any) => {
      handleOpenModalRevert(versions?.data);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    });
};

const handleGetPreviewVersionDestination = async (fileName: string, type: string) => {
  isLoadingPage.value = true;
  packageManagementStore
    .getPreviewDestinationByFileName(fileName)
    .then((data) => {
      handleSubmit(data?.data, fileName, type);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingPage.value = false;
    });
};

const handleRevertFile = async (fileName: string) => {
  isRevert.value = true;
  isLoadingImport.value = true;
  isShowModal.value = true;
  packageManagementStore
    .revertVersionDestination(fileName)
    .then(() => {
      isSuccess.value = true;
      getListDestination();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingImport.value = false;
    });
};

const handleOpenModalRevert = (versions: any) => {
  const dialogRef = dialog.open(ViewVersionRevertModal, {
    props: {
      header: t('common.buttons.revert'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      versions
    },
    emits: {
      onPreview: (fileName: string) => {
        handleGetPreviewVersionDestination(fileName, 'PREVIEW');
      },
      onRevert: (fileName: string) => {
        handleGetPreviewVersionDestination(fileName, 'REVERT');
        dialogRef.close();
      },
      onExport: (fileName: string) => {
        handleGetPreviewVersionDestination(fileName, 'EXPORT');
      },
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const getListAirports = async () => {
  airManagementStore
    .getListAir()
    .then((response) => {
      airports.value = response;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    });
};

getListAirports();

const getListDestination = () => {
  isLoadingPage.value = true;
  packageManagementStore
    .getListDestination()
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoadingPage.value = false;
    });
};

watch(
  destinations,
  (newVal: any) => {
    sheetsData.value = [
      {
        name: 'Sheet 1',
        data: formatDataExport(newVal)
      }
    ];
  },
  { deep: true }
);

onMounted(() => {
  getListDestination();
});
</script>

<template>
  <div class="destination-management">
    <LoadingSpinner v-if="isLoadingPage" />
    <div class="flex justify-content-end w-full">
      <Button v-if="getDestinationsConfiguration(authorizations)?.add_destination" id="add-destination-btn" type="button" icon="pi pi-plus" class="mt-3 mb-3" :label="$t('management.package.add_destination')" @click="editDestination()" />
    </div>
    <DataTable
      v-model:filters="filtersDest"
      :value="destinations"
      paginator
      showGridlines
      :rows="10"
      dataKey="code"
      filterDisplay="menu"
      :globalFilterFields="['name', 'nameFr', 'code', 'region', 'regionFr', 'status', 'countryName', 'countryNameFr', 'airportCode', 'cityName', 'cityNameFr', 'airportCode', 'airportName', 'airportNameFr', 'brochure']"
    >
      <template #header>
        <div class="flex justify-content-between">
          <div class="flex justify-content-left gap-3">
            <div class="">
              <Button id="clear-filter-btn" type="button" icon="pi pi-filter-slash" class="clear-destination-btn mb-2" :label="$t('common.buttons.clear')" @click="clearFilter()" />
            </div>
            <div v-if="getDestinationsConfiguration(authorizations)?.export_destination">
              <ExportExcelFile id="btn-export-excel" class="export-destination-btn" :sheets="sheetsData" :columnsStyle="sheetColumnStyle" :file-name="renderNameFileExport('Package_Destination_Management')"></ExportExcelFile>
            </div>
            <div v-if="getDestinationsConfiguration(authorizations)?.import_destination">
              <Button id="btn-import-excel" :label="$t('common.buttons.import')" class="import-destination-btn" @click="showModalImportExcel()" />
            </div>
            <div v-if="getDestinationsConfiguration(authorizations)?.revert_destination">
              <Button id="btn-revert-excel" :label="$t('common.buttons.revert')" class="revert-destination-btn" @click="showRevertVersionDestination('DESTINATION')" />
            </div>
          </div>
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText id="input-search" class="inputTextClass" v-model="filtersDest['global'].value" :placeholder="$t('my_quote.search_keyword')" />
          </span>
        </div>
      </template>
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <template #loading> {{ $t('common.generic.loading') }}. {{ $t('my_quote.please_wait') }}. </template>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="airportCode" :header="$t('management.package.airport_code')">
        <template #body="{ data }">
          {{ data.airportCode }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_airport_code')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="airportName" :header="$t('management.air.airport_name')">
        <template #body="{ data }">
          {{ data.airportName }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.airport_name')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="airportNameFr" :header="$t('management.air.airport_name_french')">
        <template #body="{ data }">
          {{ data.airportNameFr }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.airport_name_french')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="name" :header="$t('management.package.destination_name')">
        <template #body="{ data }">
          {{ data.name }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_destination')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="nameFr" :header="$t('management.package.destination_name_french')">
        <template #body="{ data }">
          {{ data.nameFr }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_destination')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="region" :header="$t('management.air.region')">
        <template #body="{ data }">
          {{ data.region }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_region')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="regionFr" :header="$t('management.air.region_french')">
        <template #body="{ data }">
          {{ data.regionFr }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_region')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="country" :header="$t('management.air.country_name')">
        <template #body="{ data }">
          {{ data.country }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_country')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="countryFr" :header="$t('management.air.country_name_french')">
        <template #body="{ data }">
          {{ data.countryFr }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_country')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="status" :header="$t('management.package.dest_status')">
        <template #body="{ data }">
          <ToggleSwitch :disabled="handleOpenChangeRole(data.status) || !getDestinationsConfiguration(authorizations)?.status_destination" v-model="data.status" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="onUpdateStatus(data)" />
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverityStatus(slotProps.option)" />
            </template>
          </Select>
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="destType" :header="$t('management.package.dest_type')">
        <template #body="{ data }"> {{ data.destType }} </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_dest_type')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="code" :header="$t('management.package.dest_sv_id')">
        <template #body="{ data }">
          {{ data.code }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.hotel.search_by_dest_sv_id')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :show-filter-operator="false" :show-add-button="false" field="brochure" :header="$t('management.package.brochure')">
        <template #body="{ data }">
          {{ data.brochure }}
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.package.brochure')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :header="$t('common.action')">
        <template #body="{ data }">
          <div class="flex">
            <Button v-if="getDestinationsConfiguration(authorizations)?.edit_destination" id="edit_destination_btn" icon="pi pi-pencil" class="p-button-text" @click="editDestination(data)" />
            <Button v-if="getDestinationsConfiguration(authorizations)?.delete_destination" id="delete_destination_btn" icon="pi pi-trash" class="p-button-text" @click="handleDeleteDestination(data)" />
          </div>
        </template>
      </TableColumn>
    </DataTable>
    <Dialog v-model:visible="isShowModal" :header="$t('common.labels.uploading_destination')" modal :closable="!isLoadingImport">
      <ProgressModal :isProgress="isLoadingImport" @handleClose="handleCloseModalProgress" :isSuccess="isSuccess" :isRevert="isRevert" />
    </Dialog>
  </div>
</template>
